import React, { useState, useEffect } from 'react';
import { Save, Send, TestTube, Mail, Server, User, Lock, AtSign, UserCheck, Users, CheckSquare, Square } from 'lucide-react';
import ApiService from '../../services/api';

interface SmtpSettings {
  id?: number;
  host: string;
  port: number;
  secure: boolean;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  isActive: boolean;
}

interface Customer {
  id: number;
  name: string;
  email: string;
  phone: string;
}

interface HomeSettings {
  id: number;
  backgroundColor?: string;
  secondaryBackgroundColor?: string;
  isActive: boolean;
}

const SmtpSettingsComponent: React.FC = () => {
  const [settings, setSettings] = useState<SmtpSettings>({
    host: '',
    port: 587,
    secure: true,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    isActive: true
  });
  const [loading, setLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Custom mail state
  const [customMailSubject, setCustomMailSubject] = useState('');
  const [customMailContent, setCustomMailContent] = useState('');
  const [customMailLoading, setCustomMailLoading] = useState(false);
  const [customMailMessage, setCustomMailMessage] = useState({ type: '', text: '' });
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [homeSettings, setHomeSettings] = useState<HomeSettings | null>(null);

  useEffect(() => {
    loadSettings();
    loadCustomers();
    loadHomeSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await ApiService.getSmtpSettings();
      if (response && response.settings) {
        setSettings(response.settings);
      }
    } catch (error) {
      console.error('SMTP settings load error:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await ApiService.getCustomers();
      if (response && response.customers) {
        setCustomers(response.customers);
      }
    } catch (error) {
      console.error('Customers load error:', error);
    }
  };

  const loadHomeSettings = async () => {
    try {
      const response = await ApiService.getHomeSettings();
      if (response && response.settings) {
        setHomeSettings(response.settings);
      }
    } catch (error) {
      console.error('Home settings load error:', error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) : value
    }));
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await ApiService.saveSmtpSettings(settings);
      
      if (response && response.success) {
        setMessage({ type: 'success', text: 'SMTP ayarları başarıyla kaydedildi' });
        setSettings(response.settings);
      } else {
        setMessage({ type: 'error', text: response?.message || 'SMTP ayarları kaydedilemedi' });
      }
    } catch (error) {
      console.error('SMTP settings save error:', error);
      setMessage({ type: 'error', text: 'SMTP ayarları kaydedilirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const handleTestMail = async () => {
    if (!testEmail) {
      setMessage({ type: 'error', text: 'Test e-posta adresi giriniz' });
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      const response = await ApiService.sendTestMail(testEmail);
      
      if (response && response.success) {
        setMessage({ type: 'success', text: 'Test maili başarıyla gönderildi' });
        setTestEmail('');
      } else {
        setMessage({ type: 'error', text: response?.message || 'Test maili gönderilemedi' });
      }
    } catch (error) {
      console.error('Test mail error:', error);
      setMessage({ type: 'error', text: 'Test maili gönderilirken hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedCustomers([]);
      setSelectAll(false);
    } else {
      setSelectedCustomers(customers.map(c => c.id));
      setSelectAll(true);
    }
  };

  const handleCustomerSelect = (customerId: number) => {
    setSelectedCustomers(prev => {
      if (prev.includes(customerId)) {
        const newSelected = prev.filter(id => id !== customerId);
        setSelectAll(newSelected.length === customers.length);
        return newSelected;
      } else {
        const newSelected = [...prev, customerId];
        setSelectAll(newSelected.length === customers.length);
        return newSelected;
      }
    });
  };

  const handleCustomMailSend = async () => {
    if (!customMailSubject.trim() || !customMailContent.trim()) {
      setCustomMailMessage({ type: 'error', text: 'Konu ve içerik alanları zorunludur' });
      return;
    }

    if (selectedCustomers.length === 0) {
      setCustomMailMessage({ type: 'error', text: 'En az bir müşteri seçiniz' });
      return;
    }

    try {
      setCustomMailLoading(true);
      setCustomMailMessage({ type: '', text: '' });

      const selectedCustomerEmails = customers
        .filter(c => selectedCustomers.includes(c.id))
        .map(c => c.email);

      const response = await ApiService.sendCustomMail({
        to: selectedCustomerEmails,
        subject: customMailSubject,
        content: customMailContent
      });

      if (response && response.success) {
        setCustomMailMessage({ 
          type: 'success', 
          text: `${selectedCustomers.length} müşteriye mail başarıyla gönderildi` 
        });
        setCustomMailSubject('');
        setCustomMailContent('');
        setSelectedCustomers([]);
        setSelectAll(false);
      } else {
        setCustomMailMessage({ type: 'error', text: response?.message || 'Mail gönderilemedi' });
      }
    } catch (error) {
      console.error('Custom mail error:', error);
      setCustomMailMessage({ type: 'error', text: 'Mail gönderilirken hata oluştu' });
    } finally {
      setCustomMailLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-light tracking-wide">Mail Ayarları</h2>
          <p className="text-gray-400 text-sm">SMTP ayarları ve toplu mail gönderimi</p>
        </div>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`p-4 border rounded ${
          message.type === 'success' 
            ? 'border-green-600 text-green-400 bg-green-900/20' 
            : 'border-red-600 text-red-400 bg-red-900/20'
        }`}>
          {message.text}
        </div>
      )}

      {/* SMTP Settings Form */}
      <div className="rounded-lg shadow-sm p-6 text-white border" style={{ 
        backgroundColor: homeSettings?.backgroundColor || '#1f2937',
        borderColor: homeSettings?.secondaryBackgroundColor || '#374151'
      }}>
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
          <Server className="w-5 h-5 mr-2" />
          SMTP Sunucu Ayarları
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Host */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Server className="w-4 h-4 mr-2" />
              SMTP Sunucu
            </label>
            <input
              type="text"
              name="host"
              value={settings.host}
              onChange={handleInputChange}
              placeholder="smtp.gmail.com"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>

          {/* Port */}
          <div>
            <label className="block text-sm font-medium mb-2">Port</label>
            <input
              type="number"
              name="port"
              value={settings.port}
              onChange={handleInputChange}
              placeholder="587"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Kullanıcı Adı
            </label>
            <input
              type="text"
              name="username"
              value={settings.username}
              onChange={handleInputChange}
              placeholder="your-email@gmail.com"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Lock className="w-4 h-4 mr-2" />
              Şifre
            </label>
            <input
              type="password"
              name="password"
              value={settings.password}
              onChange={handleInputChange}
              placeholder="App Password"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>

          {/* From Email */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <AtSign className="w-4 h-4 mr-2" />
              Gönderen E-posta
            </label>
            <input
              type="email"
              name="fromEmail"
              value={settings.fromEmail}
              onChange={handleInputChange}
              placeholder="noreply@shevastudio.com"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>

          {/* From Name */}
          <div>
            <label className="block text-sm font-medium mb-2 flex items-center">
              <UserCheck className="w-4 h-4 mr-2" />
              Gönderen Adı
            </label>
            <input
              type="text"
              name="fromName"
              value={settings.fromName}
              onChange={handleInputChange}
              placeholder="Sheva Studio"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>
        </div>

        {/* Secure Connection */}
        <div className="mt-6">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="secure"
              checked={settings.secure}
              onChange={handleInputChange}
              className="mr-2"
            />
            <span className="text-sm">Güvenli bağlantı (SSL/TLS)</span>
          </label>
        </div>

        {/* Save Button */}
        <div className="mt-6">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
          </button>
        </div>
      </div>

      {/* Test Mail Section */}
      <div className="rounded-lg shadow-sm p-6 text-white border" style={{ 
        backgroundColor: homeSettings?.backgroundColor || '#1f2937',
        borderColor: homeSettings?.secondaryBackgroundColor || '#374151'
      }}>
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
          <TestTube className="w-5 h-5 mr-2" />
          Test Maili Gönder
        </h3>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium mb-2 flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Test E-posta Adresi
            </label>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="test@example.com"
              className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={handleTestMail}
              disabled={loading || !testEmail}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              <span>{loading ? 'Gönderiliyor...' : 'Test Maili Gönder'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Custom Mail Section */}
      <div className="rounded-lg shadow-sm p-6 text-white border" style={{ 
        backgroundColor: homeSettings?.backgroundColor || '#1f2937',
        borderColor: homeSettings?.secondaryBackgroundColor || '#374151'
      }}>
        <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
          <Mail className="w-5 h-5 mr-2" />
          Toplu Mail Gönder
        </h3>

        {/* Custom Mail Message */}
        {customMailMessage.text && (
          <div className={`p-4 border rounded mb-6 ${
            customMailMessage.type === 'success' 
              ? 'border-green-600 text-green-400 bg-green-900/20' 
              : 'border-red-600 text-red-400 bg-red-900/20'
          }`}>
            {customMailMessage.text}
          </div>
        )}

        {/* Mail Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Mail Konusu</label>
              <input
                type="text"
                value={customMailSubject}
                onChange={(e) => setCustomMailSubject(e.target.value)}
                placeholder="Mail konusu..."
                className="w-full px-3 py-2 rounded text-white placeholder-gray-400 focus:outline-none"
              style={{ 
                backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151',
                borderColor: homeSettings?.backgroundColor || '#1f2937'
              }}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-2">Mail İçeriği</label>
              <textarea
                value={customMailContent}
                onChange={(e) => setCustomMailContent(e.target.value)}
                placeholder="Mail içeriği..."
                rows={8}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white placeholder-gray-400 focus:outline-none focus:border-gray-500 resize-none"
              />
            </div>

            <button
              onClick={handleCustomMailSend}
              disabled={customMailLoading || !customMailSubject.trim() || !customMailContent.trim() || selectedCustomers.length === 0}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4" />
              <span>
                {customMailLoading 
                  ? 'Gönderiliyor...' 
                  : `${selectedCustomers.length} Müşteriye Gönder`
                }
              </span>
            </button>
          </div>

          {/* Customer List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Müşteri Listesi
              </label>
              <button
                onClick={handleSelectAll}
                className="flex items-center space-x-2 px-3 py-1 text-white rounded text-sm"
                style={{ 
                  backgroundColor: homeSettings?.secondaryBackgroundColor || '#374151'
                }}
              >
                {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                <span>{selectAll ? 'Seçimi Kaldır' : 'Tümünü Seç'}</span>
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto border rounded" style={{ 
              borderColor: homeSettings?.backgroundColor || '#374151'
            }}>
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="flex items-center p-3 border-b hover:opacity-80"
                  style={{ 
                    borderColor: homeSettings?.backgroundColor || '#374151'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCustomers.includes(customer.id)}
                    onChange={() => handleCustomerSelect(customer.id)}
                    className="mr-3 cursor-pointer"
                  />
                  <div 
                    className="flex-1 cursor-pointer"
                    onClick={() => handleCustomerSelect(customer.id)}
                  >
                    <div className="text-sm font-medium">{customer.name}</div>
                    <div className="text-xs text-gray-400">{customer.email}</div>
                  </div>
                </div>
              ))}
              {customers.length === 0 && (
                <div className="p-4 text-center text-gray-400 text-sm">
                  Müşteri bulunamadı
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettingsComponent;
